/**
 * Case management API service
 *
 * Provides functions for managing cases, client profiles, field definitions,
 * team members, and quota status.
 */

import { apiClient } from './api'
import type {
  Case,
  CasesByStatus,
  CaseStatus,
  CaseTeamRole,
  CreateCaseInput,
  UpdateCaseInput,
  ListCasesParams,
  CaseQuotaStatus,
  ClientProfile,
  ClientProfileWithCaseCount,
  ClientDetailsResponse,
  CreateClientProfileInput,
  UpdateClientProfileInput,
  ListClientProfilesParams,
  DeleteClientOptions,
  CaseFieldDefinition,
  CreateCaseFieldInput,
  ListFieldsParams,
  CaseTeamMember,
  AddTeamMemberInput,
} from '../types/case-management'

// =============================================================================
// Cases API
// =============================================================================

/**
 * Build query string from params object
 */
function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue
    if (typeof value === 'string') {
      searchParams.append(key, value)
    } else if (
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      typeof value === 'bigint'
    ) {
      searchParams.append(key, value.toString())
    }
  }
  const queryString = searchParams.toString()
  return queryString ? `?${queryString}` : ''
}

/**
 * List cases with optional filtering
 */
export async function listCases(params?: ListCasesParams): Promise<Case[]> {
  const query = params ? buildQueryString(params) : ''
  return apiClient.get<Case[]>(`/cases${query}`)
}

/**
 * List cases grouped by status for Kanban display
 */
export async function listCasesGrouped(): Promise<CasesByStatus> {
  const cases = await listCases()

  const grouped: CasesByStatus = {
    collection: [],
    analysis: [],
    review: [],
    reporting: [],
    closed: [],
    archived: [],
  }

  for (const caseItem of cases) {
    if (caseItem.status in grouped) {
      grouped[caseItem.status].push(caseItem)
    }
  }

  return grouped
}

/**
 * Get a case by ID
 */
export async function getCase(id: string): Promise<Case> {
  return apiClient.get<Case>(`/cases/${id}`)
}

/**
 * Create a new case
 */
export async function createCase(input: CreateCaseInput): Promise<Case> {
  return apiClient.post<Case>('/cases', input)
}

/**
 * Update an existing case
 */
export async function updateCase(id: string, input: UpdateCaseInput): Promise<Case> {
  return apiClient.patch<Case>(`/cases/${id}`, input)
}

/**
 * Update case status only (for drag-drop in Kanban)
 */
export async function updateCaseStatus(id: string, status: CaseStatus): Promise<Case> {
  return apiClient.patch<Case>(`/cases/${id}/status`, { status })
}

/**
 * Delete a case (soft delete)
 */
export async function deleteCase(id: string): Promise<void> {
  await apiClient.delete(`/cases/${id}`)
}

// =============================================================================
// Case Quota API
// =============================================================================

/**
 * Get current user's case quota status
 */
export async function getCaseQuota(): Promise<CaseQuotaStatus> {
  return apiClient.get<CaseQuotaStatus>('/cases/quota')
}

// =============================================================================
// Case Team API
// =============================================================================

/**
 * List team members for a case
 */
export async function listCaseTeam(caseId: string): Promise<CaseTeamMember[]> {
  return apiClient.get<CaseTeamMember[]>(`/cases/${caseId}/team`)
}

/**
 * Add a team member to a case
 */
export async function addTeamMember(
  caseId: string,
  userId: string,
  role: CaseTeamRole
): Promise<CaseTeamMember> {
  const input: AddTeamMemberInput = { userId, caseRole: role }
  return apiClient.post<CaseTeamMember>(`/cases/${caseId}/team`, input)
}

/**
 * Remove a team member from a case
 */
export async function removeTeamMember(caseId: string, userId: string): Promise<void> {
  await apiClient.delete(`/cases/${caseId}/team/${userId}`)
}

// =============================================================================
// Client Profiles API
// =============================================================================

/**
 * List client profiles (simple list)
 */
export async function listClientProfiles(
  params?: ListClientProfilesParams
): Promise<ClientProfile[]> {
  const query = params ? buildQueryString(params) : ''
  return apiClient.get<ClientProfile[]>(`/client-profiles${query}`)
}

/**
 * List client profiles with case counts (for list views)
 */
export async function listClientProfilesWithCounts(
  params?: ListClientProfilesParams
): Promise<ClientProfileWithCaseCount[]> {
  const query = params ? buildQueryString(params) : ''
  return apiClient.get<ClientProfileWithCaseCount[]>(`/client-profiles/with-counts${query}`)
}

/**
 * Get a client profile by ID
 */
export async function getClientProfile(id: string): Promise<ClientProfile> {
  return apiClient.get<ClientProfile>(`/client-profiles/${id}`)
}

/**
 * Get client profile details with case summary
 */
export async function getClientProfileDetails(id: string): Promise<ClientDetailsResponse> {
  return apiClient.get<ClientDetailsResponse>(`/client-profiles/${id}/details`)
}

/**
 * Create a new client profile
 */
export async function createClientProfile(input: CreateClientProfileInput): Promise<ClientProfile> {
  return apiClient.post<ClientProfile>('/client-profiles', input)
}

/**
 * Update a client profile
 * Note: Only org_admin+ can update the name field
 */
export async function updateClientProfile(
  id: string,
  input: UpdateClientProfileInput
): Promise<ClientProfile> {
  return apiClient.patch<ClientProfile>(`/client-profiles/${id}`, input)
}

/**
 * Delete a client profile (soft delete)
 *
 * If the client has linked cases, you must provide options:
 * - reassignTo: ID of another client to reassign cases to
 * - unlinkCases: true to unlink all cases (set clientProfileId to null)
 */
export async function deleteClientProfile(
  id: string,
  options?: DeleteClientOptions
): Promise<void> {
  await apiClient.delete(`/client-profiles/${id}`, { data: options })
}

// =============================================================================
// Case Field Definitions API
// =============================================================================

/**
 * List available field definitions (system + org-specific)
 */
export async function listCaseFields(params?: ListFieldsParams): Promise<CaseFieldDefinition[]> {
  const query = params ? buildQueryString(params) : ''
  return apiClient.get<CaseFieldDefinition[]>(`/case-fields${query}`)
}

/**
 * Get a field definition by ID
 */
export async function getCaseField(id: string): Promise<CaseFieldDefinition> {
  return apiClient.get<CaseFieldDefinition>(`/case-fields/${id}`)
}

/**
 * Create a new organisation-specific field definition
 * (OrgAdmin only)
 */
export async function createCaseField(input: CreateCaseFieldInput): Promise<CaseFieldDefinition> {
  return apiClient.post<CaseFieldDefinition>('/case-fields', input)
}

/**
 * Delete a custom field definition
 * (OrgAdmin only, cannot delete system fields)
 */
export async function deleteCaseField(id: string): Promise<void> {
  await apiClient.delete(`/case-fields/${id}`)
}
